import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CodeService, type Code } from "@/services/CodeService";
import { SpeciesService, type Species } from "@/services/SpeciesService";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { setFormErrors } from "@/lib/react-form-utils";
import { ObservationService, type Observation, } from "@/services/ObservationService";
import { useParams } from "react-router";
import { AttachmentType, type AttachmentDetails, } from "@/services/AttachmenteService";
import FeatureFooter from "../feature/FeatureFooter";
import { Textarea } from "../ui/textarea";
import { FileUploadCopied } from "../ui/FilesUploadCopied";
import { Card, CardContent } from "../ui/card";
import { ValidationError } from "@/types/problemDetails";

interface ObservationNewFormProps {
  featureId: string;
  onClose: (
    observation: Observation | null,
    attachments: AttachmentDetails[]
  ) => void;
  showFooter: boolean;
}

const ObservationNewForm: React.FC<ObservationNewFormProps> = ({
  featureId,
  onClose,
  showFooter,
}) => {
  const params = useParams();
  const {
    register,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    setError,
  } = useForm<Omit<Observation, "id">>();
  const [codes, setCodes] = useState<Code[]>([]);
  const [speciesList, setSpeciesList] = useState<Species[]>([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [observationId, setObservationId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<AttachmentDetails[]>([]);
  const [newObservation, setNewObservation] = useState<Observation | null>(null);

  useEffect(() => {
    const initPage = async () => {
      const [codes, species] = await Promise.all([
        CodeService.getCodes2(),
        SpeciesService.getBySiteId(params.siteId!),
      ]);
      setCodes(codes.filter((c) => c.type === "ObservationType"));
      setSpeciesList(
        [...species]
          .filter((item) => item.active)
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    };
    initPage();
  }, [params.siteId, featureId, showFooter]);

  useEffect(() => {
    reset({
      siteId: params.siteId,
      areaId: null,
      featureId: featureId,
      name: "",
      typeCodeId: codes.find((c) => c.isDefault)?.id ?? "",
      speciesId: null,
    });

    
  }, [codes, featureId, params.siteId, reset]);

  useEffect(() => {
    setIsSaveDisabled(!isDirty);
  }, [isDirty]);

  const handleFormSubmit = async (data: Omit<Observation, "id">) => {
    try {
      setIsSaveDisabled(true);
      data.tenantId = params.tenantId!;
      const newObservation = await ObservationService.create(data);
      // let attachments: AttachmentDetails[] = [];
      // if (selectedFiles) {
      //   attachments = await AttachmentService.uploadAttachment(
      //     newObservation.id,
      //     selectedFiles
      //   );
      // }
      //onClose(newObservation, attachments);
      setNewObservation(newObservation);
      setShowFileUpload(true)
      setObservationId(newObservation.id);
    } catch (error) {
      if (error instanceof ValidationError) {
        setFormErrors<Omit<Observation, "id">>(error.errors, setError, data);
      }
    } finally {
      setIsSaveDisabled(false);
    }
  };

  return (
    <>
      {!showFileUpload ? (
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div>
            <Label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700"
            >
              Name*
            </Label>
            <Input
              type="text"
              {...register("name", { required: "Name is required" })}
            />
            <span className="text-sm text-red-500">{errors.name?.message}</span>
          </div>
          <div>
            <Label htmlFor="description">Observations</Label>
            <Textarea
              id="description"
              placeholder="Enter observations..."
              {...register("description")}
            />
          </div>       
          <div>
            <Label htmlFor="typeCodeId">Type</Label>
            <select
              id="typeCodeId"
              className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1`}
              {...register("typeCodeId", { required: "Type is required" })}
            >
              {codes.length > 0 && (
                <>
                  {codes
                    .filter((c) => c.type === "ObservationType")
                    .map((typeCode) => (
                      <option key={typeCode.id} value={typeCode.id}>
                        {typeCode.name} 
                      </option>
                    ))}
                </>
              )}
            </select>
          </div>
          <div>
            <Label htmlFor="speciesId">Species</Label>
            <select
              id="speciesId"
              className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1`}
              {...register("speciesId", { setValueAs: (value) => (value === "" ? null : value) })}
            >
              <option value="">Select a species</option>
              {speciesList.map((species) => (
                <option key={species.id} value={species.id}>
                  {species.scientificName ? `${species.name} (${species.scientificName})` : species.name}
                </option>
              ))}
            </select>
          </div>
          {showFooter && (<FeatureFooter featureId={featureId} featureTypeId={null} featureName={null} featureTypeName={null} />)}

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => onClose(null, [])}
            >
              Cancel
            </button>
            <Button
              type="submit" 
              disabled={isSaveDisabled}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Create
            </Button>
          </div>
        </form>
      ) : (
        <div>
            <FileUploadCopied
              attachmentType={AttachmentType.None}
              entityId={observationId!}
              siteId={params.siteId!}
              tenantId={params.tenantId!}
              entityType="Observation"
              buttonText="Select Images"
              onFileUploaded={(attachment) => {
                setAttachments(prev => [...prev, attachment]);
              }}
            />

            <div className="grow">
              {/* <div className={`${attachmentColumn ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4"}`}> */}
              <div className="grid grid-cols-1 gap-4">
              {attachments.map((att) => (
                <Card key={att.id} className="mt-1 pt-2 h-72 flex flex-col">
                  <CardContent className="text-sm text-gray-600 h-full flex flex-col">
                    <div className="flex flex-col h-full">
                      <img
                        src={att.url}
                        alt={att.filename}
                        className="w-full flex-1 object-cover rounded-md mb-2 min-h-0"
                        style={{ flexGrow: 1 }}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            </div>
          <button
            type="button"
            className="px-4 py-2 my-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={() => {
              if (!newObservation) {
                throw new Error("No observation created");
              }

              onClose(newObservation, attachments)
            }}
          >
            Close
          </button>
        </div>
      )}
    </>
  );
};

export default ObservationNewForm;
